import { addSheetBackLink, PARENT_SHEETS } from "../sheetNavigation.js";

export const HEADER_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FF33CCFF" },
};

export const VALUE_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFFFFFFF" },
};

export const NA_FILL = {
  type: "pattern",
  pattern: "solid",
  fgColor: { argb: "FFD9D9D9" },
};

export const THIN = { style: "thin", color: { argb: "FF000000" } };
export const THIN_BLACK = { top: THIN, left: THIN, bottom: THIN, right: THIN };

export const BODY_FONT = { name: "Calibri", size: 11 };
export const TITLE_FONT = { name: "Calibri", size: 14, bold: true };
export const HEADER_FONT_12 = { name: "Calibri", size: 12 };
export const HEADER_FONT_11 = { name: "Calibri", size: 11 };
export const SUBTITLE_FONT = { name: "Calibri", size: 11, bold: true };
export const HEADER_FONT_BOLD_11 = { name: "Calibri", size: 11, bold: true };
export const RED_LABEL_FONT = { name: "Calibri", size: 11, color: { argb: "FFFF0000" } };
export const LINK_FONT = {
  name: "Calibri",
  size: 11,
  color: { argb: "FF0563C1" },
  underline: true,
};

export const BACK_ROW = 7;
export const TITLE_ROW = 8;
export const HEADER_ROW = 9;
export const TEMPLATE_ROW = 10;
export const SPACER_ROW = 11;
export const DATA_START_ROW = 12;

export function setCell(ws, row, col, value, options = {}) {
  const cell = ws.getCell(row, col);
  cell.value = value;
  if (options.font) {
    cell.font =
      options.bold !== undefined
        ? { ...options.font, bold: options.bold }
        : options.font;
  } else {
    cell.font = options.bold ? HEADER_FONT_BOLD_11 : BODY_FONT;
  }
  if (options.border) cell.border = options.border;
  else if (options.bordered !== false) cell.border = THIN_BLACK;
  cell.alignment = {
    horizontal: options.align || "center",
    vertical: "middle",
    wrapText: options.wrapText !== false,
  };
  if (options.fill) cell.fill = options.fill;
}

export function applyColumnLayout(ws, widths, hiddenCols = ["A", "B", "K", "L"]) {
  Object.entries(widths).forEach(([col, width]) => {
    ws.getColumn(col).width = width;
  });
  hiddenCols.forEach((col) => {
    ws.getColumn(col).hidden = true;
  });
}

/** Hide every column after `lastVisibleCol` (1-based, e.g. 10 = J). */
export function hideColumnsAfter(ws, lastVisibleCol, { width = 9.1796875 } = {}) {
  for (let col = lastVisibleCol + 1; col <= 16384; col += 1) {
    const column = ws.getColumn(col);
    column.width = width;
    column.hidden = true;
  }
}

/** Hide rows 1..lastHiddenRow (set height so ExcelJS keeps them). */
export function hideRowsThrough(ws, lastHiddenRow, { height = 15 } = {}) {
  for (let r = 1; r <= lastHiddenRow; r += 1) {
    const row = ws.getRow(r);
    row.height = height;
    row.hidden = true;
  }
}

export function setupSheetView(ws, { ySplit = 11, topLeftCell = "D12", zoomScale = 80, xSplit = 1 } = {}) {
  ws.views = [
    {
      showGridLines: false,
      state: "frozen",
      xSplit,
      ySplit,
      topLeftCell,
      zoomScale,
    },
  ];
}

export function addBackLink(ws, colEnd, options = {}) {
  addSheetBackLink(ws, PARENT_SHEETS.GENERAL_DISCLOSURES, {
    row: BACK_ROW,
    col: colEnd,
    align: "right",
    ...options,
  });
}

export function addTitleRow(ws, colStart, colEnd, title, { height = 30 } = {}) {
  ws.mergeCells(TITLE_ROW, colStart, TITLE_ROW, colEnd);
  const cell = ws.getCell(TITLE_ROW, colStart);
  cell.value = title;
  cell.font = TITLE_FONT;
  cell.fill = HEADER_FILL;
  cell.border = THIN_BLACK;
  cell.alignment = { horizontal: "center", vertical: "middle", wrapText: true };
  ws.getRow(TITLE_ROW).height = height;
}

export function addSubtitleRow(ws, colStart, colEnd, text, { height = 35 } = {}) {
  ws.mergeCells(HEADER_ROW, colStart, HEADER_ROW, colEnd);
  const cell = ws.getCell(HEADER_ROW, colStart);
  cell.value = text;
  cell.font = SUBTITLE_FONT;
  cell.border = THIN_BLACK;
  cell.alignment = { horizontal: "left", vertical: "middle", wrapText: true };
  ws.getRow(HEADER_ROW).height = height;
}

export function borderFourColHeaderRow(ws, row) {
  ws.getCell(row, 4).border = THIN_BLACK;
  ws.getCell(row, 5).border = { top: THIN, bottom: THIN, right: THIN };
  ws.getCell(row, 6).border = THIN_BLACK;
  ws.getCell(row, 7).border = THIN_BLACK;
}

export function borderTableRow(ws, row, colStart, colEnd) {
  for (let col = colStart; col <= colEnd; col += 1) {
    ws.getCell(row, col).border = THIN_BLACK;
  }
}

export function addHiddenTemplateRow(ws, colStart, colEnd, { height = 15.5, naCols = [] } = {}) {
  borderTableRow(ws, TEMPLATE_ROW, colStart, colEnd);
  for (let col = colStart; col <= colEnd; col += 1) {
    const fill = naCols.includes(col) ? NA_FILL : undefined;
    setCell(ws, TEMPLATE_ROW, col, "", { bordered: false, fill });
  }
  ws.getRow(TEMPLATE_ROW).height = height;
  ws.getRow(TEMPLATE_ROW).hidden = true;
}

export function addSpacerRow(ws, colStart, colEnd, { height = 30 } = {}) {
  ws.mergeCells(SPACER_ROW, colStart, SPACER_ROW, colEnd);
  ws.getCell(SPACER_ROW, colStart).border = { top: THIN, left: THIN, bottom: THIN, right: THIN };
  ws.getRow(SPACER_ROW).height = height;
}

export function setValueDataRow(ws, row, colStart, colEnd, values, columnDefs) {
  borderTableRow(ws, row, colStart, colEnd);
  columnDefs.forEach(({ col, value, align = "left", fill = VALUE_FILL, font = BODY_FONT }) => {
    setCell(ws, row, col, value ?? "", {
      align,
      fill,
      font,
      bordered: false,
    });
  });
}
